import { useTranslation } from 'react-i18next';

import { Text } from '@kognitos/lattice';
import type { V1RunState } from '@/generated/hooks/jeeves/models';
import {
  DATE_FORMAT_TOKENS,
  formatRelativeTime,
  formatTimestamp,
} from '@/shared/utils/datetime-utils';

/**
 * Step counter for runs whose steps are individually addressable (Quill V2).
 */
export interface IRunStepProgress {
  /** 1-based number of the step executing now. */
  current: number;
  /** Total steps in the automation's AOP. */
  total: number;
}

/**
 * Props for the RunProgressIndicator component.
 */
interface IRunProgressIndicatorProps {
  /** The current run state */
  runState: V1RunState | undefined;
  /** The run progress percentage (0-100) */
  progress: number;
  /**
   * Step counter, preferred over `progress` when present. Null for runs with no
   * addressable steps (no manifest), which keep the percentage.
   */
  stepProgress?: IRunStepProgress | null;
  /**
   * Whether the first AI completion has been received.
   * When false, the paused state is suppressed even if awaiting_guidance is set,
   * because the run is still processing its first response.
   * Defaults to true for backward compatibility.
   */
  hasReceivedFirstCompletion?: boolean;
}

/**
 * Displays the run progress indicator based on the current run state.
 *
 * Shows completion/failure/paused status text, or — while the run executes —
 * which step it is on (`Step 3 of 7`). Runs whose steps aren't individually
 * addressable have no `stepProgress` and fall back to a percentage.
 *
 * @param {IRunProgressIndicatorProps} props - Component props
 * @returns {JSX.Element} Progress indicator component
 */
export function RunProgressIndicator({
  runState,
  progress,
  stepProgress,
  hasReceivedFirstCompletion = true,
}: IRunProgressIndicatorProps) {
  const { t: tAutomation } = useTranslation('automation');

  const cappedProgress = Math.min(progress, 99);
  const isAwaitingGuidance =
    Boolean(runState?.awaiting_guidance) && hasReceivedFirstCompletion;

  if (runState?.completed) {
    const completedAt = runState.update_time;
    return (
      <Text
        level="small"
        color="muted"
        data-slot="completion-status"
        data-testid="completion-status"
      >
        {completedAt
          ? tAutomation('automation.run.page.header.completedAt', {
              relativeTime: formatRelativeTime(completedAt),
              time: formatTimestamp(
                completedAt,
                DATE_FORMAT_TOKENS.TIME_WITH_SECONDS,
              ),
            })
          : tAutomation('automation.run.page.header.completed')}
      </Text>
    );
  }

  if (runState?.failed) {
    return (
      <Text level="small" color="muted" data-slot="failure-status">
        {tAutomation('automation.run.page.header.failed')}
      </Text>
    );
  }

  if (runState?.stopped || isAwaitingGuidance) {
    return (
      <Text level="small" color="muted" data-slot="paused-status">
        {tAutomation('automation.run.page.header.paused')}
      </Text>
    );
  }

  if (stepProgress) {
    return (
      <Text level="small" color="muted" data-slot="running-status">
        {tAutomation('automation.run.page.header.runningStep', {
          current: stepProgress.current,
          total: stepProgress.total,
        })}
      </Text>
    );
  }

  return (
    <Text level="small" color="muted" data-slot="running-status">
      {tAutomation('automation.run.page.header.running', {
        progress: cappedProgress,
      })}
    </Text>
  );
}
